import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { CheckCircle2, Award, TrendingUp, Calendar, Loader2 } from "lucide-react";
import { muscleGroupLabels } from "../data/exerciseLibrary";
import ShareButton from "../components/ShareButton";

const fmtWeek = (iso) => {
  const start = new Date(iso);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const opts = { month: "short", day: "numeric" };
  return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
};

const isThisWeek = (iso) => {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
  monday.setHours(0, 0, 0, 0);
  return monday.toISOString().slice(0, 10) === iso;
};

export default function HistoryPage() {
  const [weeks, setWeeks] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/workouts/history?weeks=12").then((r) => setWeeks(r.data.weeks || [])),
      api.get("/workouts/personal-records").then((r) => setRecords(r.data.records || [])),
    ])
      .catch((err) => console.error("history load failed", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="history-page">
      <div className="mb-10">
        <div className="label-eyebrow text-[#6B9BFF] mb-2">History</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none">Your training timeline</h1>
        <p className="text-white/60 mt-3 max-w-2xl">
          Every week of logged sets, top lifts, and personal records — see your progression at a glance.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 text-sm py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Personal Records column */}
          <aside className="lg:col-span-1">
            <div className="border border-white/10 bg-[#121212] p-6 sticky top-6" data-testid="pr-list">
              <div className="flex items-center gap-2 mb-5">
                <Award className="w-4 h-4 text-[#6B9BFF]" />
                <div className="label-eyebrow text-white/50">Personal Records</div>
              </div>
              {records.length === 0 ? (
                <div className="text-sm text-white/40 italic">
                  No PRs yet. Log a set with weight and reps to start your records.
                </div>
              ) : (
                <ul className="space-y-3">
                  {records.slice(0, 10).map((pr) => (
                    <li key={pr.exercise_key} className="border-l-2 border-[#6B9BFF] pl-3 flex items-start justify-between gap-2" data-testid={`pr-${pr.exercise_key}`}>
                      <div className="min-w-0">
                        <div className="font-bold text-white text-sm">{pr.exercise_name}</div>
                        <div className="text-xs text-white/60 mt-0.5">
                          {pr.best_set?.weight_kg}kg × {pr.best_set?.reps} ·{" "}
                          <span className="text-[#6B9BFF] font-bold">{pr.best_1rm}kg 1RM</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">
                          {new Date(pr.achieved_on).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>
                      </div>
                      <ShareButton
                        text={`New PR on ${pr.exercise_name}: ${pr.best_set?.weight_kg}kg × ${pr.best_set?.reps} (${pr.best_1rm}kg 1RM) 💪`}
                        title={`PR: ${pr.exercise_name}`}
                        testid={`share-pr-${pr.exercise_key}`}
                        card={{
                          type: "pr",
                          payload: {
                            exercise: pr.exercise_name,
                            weight_kg: pr.best_set?.weight_kg,
                            reps: pr.best_set?.reps,
                            est_1rm: pr.best_1rm,
                            achieved_at: pr.achieved_on,
                          },
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>

          {/* Weeks timeline */}
          <div className="lg:col-span-2 space-y-5">
            {weeks.length === 0 ? (
              <div className="border border-white/10 bg-[#121212] p-10 text-center">
                <Calendar className="w-8 h-8 text-white/30 mx-auto mb-3" />
                <div className="font-display text-2xl mb-2">No history yet</div>
                <p className="text-white/60 text-sm mb-5 max-w-md mx-auto">
                  Log your first set on the workout page to start building your training timeline.
                </p>
                <Link
                  to="/app/workout"
                  className="inline-flex bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white"
                  data-testid="history-go-workout"
                >
                  Go to workout
                </Link>
              </div>
            ) : (
              weeks.map((w) => (
                <article
                  key={w.week_start}
                  className="border border-white/10 bg-[#121212] p-6"
                  data-testid={`history-week-${w.week_start}`}
                >
                  <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
                    <div>
                      <div className="label-eyebrow text-[#6B9BFF]">
                        {isThisWeek(w.week_start) ? "This week" : "Week of"}
                      </div>
                      <div className="font-display text-2xl mt-1">{fmtWeek(w.week_start)}</div>
                    </div>
                    <div className="flex items-center gap-5 text-xs">
                      <div className="text-right">
                        <div className="label-eyebrow text-white/40">Sessions</div>
                        <div className="font-display text-2xl text-white">
                          {w.sessions || 0}
                          <CheckCircle2 className="w-4 h-4 inline-block ml-1 text-[#6B9BFF]" />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="label-eyebrow text-white/40">Total volume</div>
                        <div className="font-display text-2xl text-white">
                          {Math.round(w.total_volume).toLocaleString()}
                          <span className="text-sm text-white/40 ml-1">kg</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {w.exercises?.length === 0 ? (
                    <div className="text-sm text-white/40 italic">No exercises logged.</div>
                  ) : (
                    <ul className="divide-y divide-white/5">
                      {w.exercises.map((e) => (
                        <li
                          key={e.exercise_key}
                          className="py-3 flex items-center gap-3"
                          data-testid={`history-ex-${w.week_start}-${e.exercise_key}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-white truncate">{e.exercise_name}</span>
                              {e.muscle_group && (
                                <span className="text-[10px] uppercase tracking-widest text-white/50 border border-white/10 px-1.5 py-0.5">
                                  {muscleGroupLabels[e.muscle_group] || e.muscle_group}
                                </span>
                              )}
                              {e.is_pr_this_week && (
                                <span className="text-[10px] uppercase tracking-widest text-[#6B9BFF] border border-[#6B9BFF] px-1.5 py-0.5 inline-flex items-center gap-1">
                                  <TrendingUp className="w-3 h-3" /> PR
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-white/50 mt-1">
                              {e.total_sets} {e.total_sets === 1 ? "set" : "sets"}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            {e.best_hold > 0 ? (
                              <div className="font-display text-lg text-white">{e.best_hold}s</div>
                            ) : e.top_weight > 0 ? (
                              <div className="font-display text-lg text-white">
                                {e.top_weight}kg <span className="text-white/40 text-sm">× {e.top_reps}</span>
                              </div>
                            ) : (
                              <div className="font-display text-lg text-white">
                                {e.top_reps} <span className="text-white/40 text-sm">reps</span>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
